import { useEffect, useRef, useState, type PropsWithChildren, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/AppStore';
import { navigationFor, navigationItemActive, type NavigationIcon, type NavigationItem, type NavigationRole } from '../navigation/registry';
import { SoundNavigationControl, SoundSettingsControl } from '../sound/SoundSettingsControl';
import { SnowsAssistantControl } from '../reactions/SnowsAssistantControl';

interface AppFrameProps {
  title?: string;
  backTo?: string;
  showBottomNav?: boolean;
  navigation?: NavigationRole;
  screenClass?: string;
}

export function AppFrame({ children, title, backTo, showBottomNav = false, navigation, screenClass = '' }: PropsWithChildren<AppFrameProps>) {
  const navRole = navigation ?? (showBottomNav ? 'child' : undefined);
  const soundIntegratedInMobileNav = navRole === 'child' || navRole === 'parent';
  useEffect(() => { document.title = title ? `${title} | MY real Money` : 'MY real Money'; }, [title]);
  return (
    <main className={`app-shell ${navRole ? `has-responsive-navigation role-${navRole}` : ''}`}>
      <a className="skip-link" href="#main-content">본문 바로가기</a>
      <section className={`${navRole ? 'responsive-frame' : 'phone-frame'} visual-system-1-9 visual-system-1-91 visual-system-1-95 visual-system-1-96 visual-system-1-97 visual-system-1-98 ${screenClass}`} aria-label={title ?? 'MY real Money'}>
        {navRole && <DesktopSidebar role={navRole} />}
        {(navRole === 'child' || navRole === 'parent') && <SiteMenuDrawer role={navRole} />}
        <div className="app-main-column">
          {title && (
            <header className="page-header">
              {backTo ? <NavLink className="icon-button" to={backTo} aria-label="이전 화면">‹</NavLink> : <span className="header-spacer" />}
              <h1>{title}</h1>
              <span className={`header-mascot ${navRole ?? 'general'}`} aria-hidden="true">{navRole === 'child' ? <img src="/assets/characters/snows/snows_wave_v1_512.png" alt="" /> : navRole === 'parent' ? <img src="/assets/characters/sage/sage_checklist_v1.png" alt="" /> : <img src="/assets/ui-icons/mrm_ui_10a_streakreward_v01.png" alt="" />}</span>
            </header>
          )}
          {navRole === 'admin' && <MobileAdminNavigation />}
          <div id="main-content" tabIndex={-1} className={`page-content ${navRole ? 'with-responsive-navigation' : ''}`}>{children}</div>
          <SoundSettingsControl withBottomNavigation={Boolean(navRole) && !soundIntegratedInMobileNav} desktopOnly={soundIntegratedInMobileNav} />
          {navRole && <MobileNavigation role={navRole} />}
        </div>
      </section>
    </main>
  );
}


const siteMenuGroups: Record<'child' | 'parent', { title: string; items: { label: string; to: string }[] }[]> = {
  child: [
    { title:'오늘', items:[{label:'오늘의 홈',to:'/child/home'},{label:'오늘의 경제 탐험',to:'/today/article'},{label:'돈 기록',to:'/child/records'},{label:'가족 미션',to:'/child/missions'}] },
    { title:'월드', items:[{label:'월드 허브',to:'/child/world'},{label:'경제도서관',to:'/child/concepts'},{label:'뉴스 스퀘어',to:'/child/concepts/economy'},{label:'월드 여정',to:'/child/world-journey'}] },
    { title:'성장', items:[{label:'스노우빌리지',to:'/village/hub'},{label:'나의 이글루',to:'/village'},{label:'월간 퍼즐',to:'/collection/puzzle'},{label:'첫 7일',to:'/child/first-week'},{label:'주간 성장',to:'/child/weekly-expedition'},{label:'주간 이야기',to:'/child/weekly-report'},{label:'금융 패스포트',to:'/child/passport'},{label:'배지·수료증',to:'/child/hall-of-honor'}] },
    { title:'가족', items:[{label:'가족 탐험대',to:'/child/family-expedition'},{label:'가족 미션',to:'/child/family-missions'},{label:'냥장고',to:'/child/fridge'},{label:'가족 대화',to:'/14'}] },
  ],
  parent: [
    { title:'확인', items:[{label:'부모 홈',to:'/parent/home'},{label:'미션 승인',to:'/parent/approvals'},{label:'현실 탐험 확인',to:'/parent/passport'}] },
    { title:'성장', items:[{label:'주간 성장 인사이트',to:'/parent/weekly-insight'},{label:'주간 리포트',to:'/parent/reports'},{label:'활동 기록',to:'/parent/records'}] },
    { title:'가족', items:[{label:'가족 탐험대',to:'/parent/family-expedition'},{label:'가족 미션·보상',to:'/parent/rewards'},{label:'응원과 대화',to:'/parent/family-talk'}] },
    { title:'설정', items:[{label:'자녀 PIN',to:'/security/pin'},{label:'운영 로그인',to:'/login'},{label:'MRM 체험',to:'/demo'}] },
  ],
};

function SiteMenuDrawer({ role }: { role: 'child' | 'parent' }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const closeMenu = () => {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    const focusable = () => [...(panel?.querySelectorAll<HTMLElement>('button, a[href], summary, [tabindex]:not([tabindex="-1"])') ?? [])]
      .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
    window.requestAnimationFrame(() => focusable()[0]?.focus());
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const groups = siteMenuGroups[role];
  const currentItem = groups.flatMap((group) => group.items).find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));

  return <div className={`site-menu-drawer-2-8 role-${role} ${open ? 'open' : ''}`}>
    <button ref={triggerRef} className="site-menu-trigger-2-8" data-reaction="navigation.menu-open" type="button" aria-label="전체 메뉴 열기" aria-expanded={open} aria-controls={`${role}-site-menu`} onClick={() => setOpen(true)}><span aria-hidden="true">☰</span><strong>전체 메뉴</strong></button>
    {open && <button className="site-menu-backdrop-2-8" type="button" aria-label="전체 메뉴 닫기" onClick={closeMenu} />}
    <aside ref={panelRef} id={`${role}-site-menu`} className="site-menu-panel-2-8" role="dialog" aria-modal="true" aria-label={`${role === 'child' ? '아이' : '부모'} 전체 메뉴`} aria-hidden={!open}>
      <header><div><small>MY real Money</small><strong>{role === 'child' ? '모든 곳 보기' : '부모 전체 메뉴'}</strong></div><button type="button" onClick={closeMenu}>닫기</button></header>
      <p className="site-menu-location-2-8"><small>현재 위치</small><strong>{currentItem?.label ?? (role === 'child' ? '오늘의 홈' : '부모 홈')}</strong></p>
      <nav>{groups.map((group) => {
        const activeGroup = group.items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
        return <details key={group.title} open={activeGroup}><summary><span>{group.title}</span><b aria-hidden="true">⌄</b></summary><div>{group.items.map((item) => {
          const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
          return <NavLink key={item.to} to={item.to} onClick={closeMenu} aria-current={active ? 'page' : undefined} className={active ? 'active' : ''}><span>{item.label}</span><b aria-hidden="true">›</b></NavLink>;
        })}</div></details>;
      })}</nav>
      <footer><NavLink to="/" onClick={closeMenu}>시작 화면</NavLink><NavLink to="/demo" onClick={closeMenu}>데모 체험</NavLink><NavLink to="/login" onClick={closeMenu}>계정·설정</NavLink></footer>
    </aside>
  </div>;
}

export function Card({ children, className = '' }: PropsWithChildren<{ className?: string }>) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function PrimaryButton({ children, onClick, type = 'button', disabled = false }: PropsWithChildren<{ onClick?: () => void; type?: 'button' | 'submit'; disabled?: boolean }>) {
  return <button className="primary-button" type={type} onClick={onClick} disabled={disabled}>{children}</button>;
}

export function SecondaryButton({ children, onClick }: PropsWithChildren<{ onClick?: () => void }>) {
  return <button className="secondary-button" type="button" onClick={onClick}>{children}</button>;
}

export function Field({ label, icon, children }: PropsWithChildren<{ label: string; icon?: string }>) {
  return (
    <label className="field">
      <span className="field-label">{icon && <img src={icon} alt="" />} {label}</span>
      {children}
    </label>
  );
}

export function Character({ src, alt, className = '', priority = false }: { src: string; alt: string; className?: string; priority?: boolean }) {
  return <img className={`character ${className}`} src={src} alt={alt} decoding="async" loading={priority ? 'eager' : 'lazy'} draggable={false} data-character-asset={src} />;
}

export function StatusBanner() {
  const { loading, error } = useAppStore();
  if (loading) return <div className="status-banner loading" role="status" aria-live="polite">저장하고 있어요…</div>;
  if (error) return <div className="status-banner error" role="alert">{error}</div>;
  return null;
}

export function ProgressBar({ value }: { value: number }) {
  return <div className="progress-track" role="progressbar" aria-label="진행률" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${value}%` }} /></div>;
}

function badgeValue(item: NavigationItem, pendingMissions: number): number | undefined {
  if (item.badgeKey === 'pendingMissions' && pendingMissions > 0) return pendingMissions;
  return undefined;
}

function NavigationLink({ item, mode, pendingMissions }: { item: NavigationItem; mode: 'mobile' | 'desktop'; pendingMissions: number }) {
  const { pathname } = useLocation();
  const active = navigationItemActive(item, pathname);
  const badge = badgeValue(item, pendingMissions);
  const label = mode === 'mobile' ? (item.shortLabel ?? item.label) : item.label;
  return (
    <NavLink
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={`${mode === 'mobile' ? 'bottom-nav-item' : 'sidebar-nav-item'} ${active ? 'active' : ''} ${item.featured ? 'mission-item' : ''}`}
    >
      {item.featured && mode === 'mobile'
        ? <span className="mission-orb"><img src="/assets/navigation/nav_mission_haneul_v1.png" alt="" /></span>
        : <span className="navigation-icon"><NavIcon name={item.icon} /></span>}
      <span className="navigation-label">{label}</span>
      {badge !== undefined && <span className="navigation-badge" aria-label={`${badge}건 대기`}>{badge}</span>}
    </NavLink>
  );
}

export function MobileNavigation({ role }: { role: NavigationRole }) {
  const { state } = useAppStore();
  const pendingMissions = state?.missions.filter((mission) => mission.status === 'submitted').length ?? 0;
  const items = navigationFor(role);
  if (role === 'admin') return null;
  if (role === 'child') {
    const firstHalf = items.slice(0, 2);
    const secondHalf = items.slice(2);
    return (
      <nav className="bottom-nav bottom-nav-child bottom-nav-child-assistant" aria-label="자녀 하단 메뉴">
        {firstHalf.map((item) => <NavigationLink key={item.id} item={item} mode="mobile" pendingMissions={pendingMissions} />)}
        <SnowsAssistantControl />
        {secondHalf.map((item) => <NavigationLink key={item.id} item={item} mode="mobile" pendingMissions={pendingMissions} />)}
      </nav>
    );
  }
  return (
    <nav className={`bottom-nav bottom-nav-${role}`} aria-label={`${role === 'parent' ? '부모' : role === 'teacher' ? '교사' : '기관'} 하단 메뉴`}>
      {items.map((item) => <NavigationLink key={item.id} item={item} mode="mobile" pendingMissions={pendingMissions} />)}
      {role === 'parent' && <SoundNavigationControl />}
    </nav>
  );
}

export function MobileAdminNavigation() {
  const { pathname } = useLocation();
  const items = navigationFor('admin');
  const groups = [...new Set(items.map((item) => item.group).filter(Boolean))] as string[];
  const current = items.find((item) => navigationItemActive(item, pathname));
  return (
    <details className="admin-mobile-menu">
      <summary><span><NavIcon name={current?.icon ?? 'home'} /></span><strong>{current?.label ?? '관리 메뉴'}</strong><small>전체 메뉴</small></summary>
      <div className="admin-mobile-menu-panel">
        {groups.map((group) => <section key={group}><h2>{group}</h2>{items.filter((item) => item.group === group).map((item) => <NavLink key={item.id} to={item.to} className={navigationItemActive(item, pathname) ? 'active' : ''}><NavIcon name={item.icon} /><span>{item.label}</span></NavLink>)}</section>)}
      </div>
    </details>
  );
}

export function DesktopSidebar({ role }: { role: NavigationRole }) {
  const { state } = useAppStore();
  const pendingMissions = state?.missions.filter((mission) => mission.status === 'submitted').length ?? 0;
  const items = navigationFor(role);
  const groups = [...new Set(items.map((item) => item.group).filter(Boolean))] as string[];
  return (
    <aside className={`desktop-sidebar desktop-sidebar-${role}`} aria-label={`${role} 데스크톱 메뉴`}>
      <div className="sidebar-brand"><span className="brand-mark">MY</span><strong>MY real Money</strong><small>{role === 'child' ? '스노우 월드 탐험' : role === 'parent' ? '부모 대시보드' : role === 'teacher' ? '교사 워크스페이스' : role === 'institution' ? '기관 운영 콘솔' : '운영 관리자'}</small></div>
      {role === 'admin' ? groups.map((group) => (
        <section className="sidebar-group" key={group}>
          <h2>{group}</h2>
          {items.filter((item) => item.group === group).map((item) => <NavigationLink key={item.id} item={item} mode="desktop" pendingMissions={pendingMissions} />)}
        </section>
      )) : <nav className="sidebar-primary-nav">{items.map((item) => <NavigationLink key={item.id} item={item} mode="desktop" pendingMissions={pendingMissions} />)}</nav>}
      <div className="sidebar-profile">
        {role === 'child'
          ? <img src="/assets/characters/snows/snows_wave_v1_512.png" alt="" />
          : <span className="sidebar-avatar" aria-hidden="true">{(state?.user.name ?? (role === 'parent' ? '보호자' : role === 'teacher' ? '교사' : role === 'institution' ? '기관' : '운영자')).slice(0,1)}</span>}
        <div><strong>{state?.user.name ?? 'MY real Money'}</strong><small>{role === 'child' ? state?.child.nickname : role === 'parent' ? '보호자 계정' : role === 'teacher' ? '교사 계정' : role === 'institution' ? '기관 계정' : '운영 계정'}</small></div>
      </div>
    </aside>
  );
}

function NavIcon({ name }: { name: NavigationIcon }) {
  const common = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  const paths: Record<NavigationIcon, ReactNode> = {
    home: <><path d="m3 11 9-7 9 7"/><path d="M5.5 10.5V21h13V10.5"/><path d="M9.5 21v-6h5v6"/></>,
    record: <><path d="M5 3.5h11a2 2 0 0 1 2 2V21H7a2 2 0 0 1-2-2Z"/><path d="M8 7h7M8 11h7M8 15h4"/><path d="m15.5 17.5 4-4 1.5 1.5-4 4-2 .5Z"/></>,
    mission: <><path d="M12 3 14.7 8.5 21 9.4l-4.5 4.4 1.1 6.2-5.6-2.9L6.4 20l1.1-6.2L3 9.4l6.3-.9Z"/></>,
    concept: <><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H12v18H7.5A3.5 3.5 0 0 0 4 23Z"/><path d="M20 5.5A3.5 3.5 0 0 0 16.5 2H12v18h4.5A3.5 3.5 0 0 1 20 23Z"/></>,
    badge: <><circle cx="12" cy="9" r="5"/><path d="m8.5 13-1 8 4.5-2 4.5 2-1-8"/><path d="m12 6 1 2 2 .3-1.5 1.5.4 2.2-1.9-1-1.9 1 .4-2.2L9 8.3 11 8Z"/></>,
    profile: <><circle cx="12" cy="8" r="4"/><path d="M4.5 21a7.5 7.5 0 0 1 15 0"/></>,
    approval: <><rect x="4" y="3" width="16" height="18" rx="2"/><path d="m8 12 2.5 2.5L16 9"/></>,
    reward: <><rect x="3" y="8" width="18" height="13" rx="2"/><path d="M12 8v13M3 12h18"/><path d="M12 8H7.5A2.5 2.5 0 1 1 10 5.5ZM12 8h4.5A2.5 2.5 0 1 0 14 5.5Z"/></>,
    report: <><path d="M4 20V10M10 20V4M16 20v-7M22 20V7"/><path d="M2 20h21"/></>,
    talk: <><path d="M4 5h16v11H9l-5 4Z"/><path d="M8 9h8M8 12h5"/></>,
    users: <><circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><circle cx="18" cy="9" r="2"/><path d="M16 14.5a5 5 0 0 1 5 5"/></>,
    content: <><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 7h8M8 11h8M8 15h5"/></>,
    asset: <><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9Z"/><path d="m4 7.5 8 4.5 8-4.5M12 12v9"/></>,
    analytics: <><path d="M4 19V9M10 19V5M16 19v-7M22 19V3"/></>,
    audit: <><path d="M4 4h16v16H4Z"/><path d="M8 8h8M8 12h8M8 16h4"/><path d="m15 16 1.5 1.5L20 14"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9A1.7 1.7 0 0 0 21 10h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></>,
    permissions: <><path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6Z"/><path d="m9 12 2 2 4-4"/></>,
    notice: <><path d="M4 13h3l9 4V7l-9 4H4Z"/><path d="M7 13v5M19 9v6"/></>,
    module: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
    school: <><path d="m3 10 9-5 9 5-9 5Z"/><path d="M6 12v6h12v-6M9 18v-4h6v4"/></>,
    assignment: <><rect x="5" y="3" width="14" height="18" rx="2"/><path d="M9 3.5h6v3H9zM8 11h8M8 15h5"/></>,
    team: <><circle cx="8" cy="8" r="3"/><circle cx="17" cy="9" r="2.5"/><path d="M2.5 20a5.5 5.5 0 0 1 11 0M13 20a4.5 4.5 0 0 1 9 0"/></>,
    village: <><path d="M4 20v-8l8-6 8 6v8"/><path d="M8 20v-5h8v5"/><path d="M6 10c1-5 11-5 12 0"/><path d="M3 20h18"/></>,
    collection: <><rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><path d="m16.5 13 1 2 2.2.3-1.6 1.6.4 2.2-2-1.1-2 1.1.4-2.2-1.6-1.6 2.2-.3Z"/></>
  };
  return <svg {...common}>{paths[name]}</svg>;
}

export function Metric({ label, value, note }: { label: string; value: ReactNode; note?: string }) {
  return <div className="metric"><span>{label}</span><strong>{value}</strong>{note && <small>{note}</small>}</div>;
}

export function RoleSwitcher() {
  const { state, setRole } = useAppStore();
  const enabled = import.meta.env.VITE_DEMO_ROLE_SWITCHER === 'true';
  if (!state || !enabled) return null;
  return (
    <div className="role-switcher" aria-label="개발용 역할 전환">
      <span>DEMO</span>
      <button className={state.session.role === 'child' ? 'active' : ''} type="button" onClick={() => void setRole('child')}>자녀</button>
      <button className={state.session.role === 'parent' ? 'active' : ''} type="button" onClick={() => void setRole('parent')}>부모</button>
      <button className={state.session.role === 'teacher' ? 'active' : ''} type="button" onClick={() => void setRole('teacher')}>교사</button>
      <button className={state.session.role === 'institution' ? 'active' : ''} type="button" onClick={() => void setRole('institution')}>기관</button>
      <button className={state.session.role === 'admin' ? 'active' : ''} type="button" onClick={() => void setRole('admin')}>관리자</button>
    </div>
  );
}
